<template>
  <div>
    <template v-if="isLoading">
      <div
        v-for="(skeleton, index) in itemsPerPage"
        :key="index"
        class="skeleton-loader my-2"
      ></div>
    </template>

    <z-pagination
      v-else
      :items-per-page="itemsPerPage"
      :items="items"
      auto-pagination
    >
      <template #data="{paginatedItems}">
        <div class="card-list-container">
          <div v-if="items.length === 0">Nenhum item</div>

          <card-list-item
            v-for="(item, index) of paginatedItems"
            :key="item.id || index"
            :title="item.title"
            :dark="item.dark"
            :draggable="item.draggable"
            :actions="item.actions"
            :background-color="draggingItem === item ? '#cecece' : null"
            :is-loading="isLoading"
            @dragstart="onDragStart(item, $event)"
            @drag="onDragging(item, $event)"
            @dragend="onDragEnd(item, $event)"
            @dragenter="onDragEnter(item, $event)"
            @dragleave="onDragLeave(item, $event)"
            @dragover="onDragOver(item, $event)"
            @drop="onDrop(item, $event)"
            @action="onClickAction(item, $event)"
            @select="onClickSelect(item, $event)"
            @click="onClickCard(item)"
          >
            <!--@slot Slot de conteúdo do card 
                @binding {object} item Objeto referente ao intem do card
                @binding {number} index Index correspondente ao card
           -->
            <slot name="content" :item="item" :index="index"> </slot>
          </card-list-item>
        </div>
      </template>
    </z-pagination>
  </div>
</template>

<script>
import CardListItem from './CardListItem/CardListItem.vue'

/**
 * @displayName Card List
 */
export default {
  name: 'ZCardList',
  components: {CardListItem},
  props: {
    /**
     * Itens da lista.
     *
     * @typedef Object {id: any, title: string, draggable: boolean, actions: array}
     */
    items: {
      type: Array,
    },
    /**
     * Quantidade de itens a serem exibidos na paginação
     */
    itemsPerPage: {
      type: Number,
      required: true,
    },
    /**
     * Aplica estado de loading na lista
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      draggingItem: null,
    }
  },

  methods: {
    onDragStart(item, event) {
      if (item.draggable) {
        /**
         * Evento emitido ao iniciar a ação de dragging
         *
         * @property {Object} item Objeto referente ao item do card
         * @property {Object} event Evento do drag
         */
        this.$emit('dragstart', {item, event})
      }
    },

    onDragging(item, event) {
      if (item.draggable) {
        /**
         * Evento emitido durante a ação de dragging
         *
         * @property {Object} item Objeto referente ao item do card
         * @property {Object} event Evento do drag
         */
        this.$emit('drag', {item, event})
      }
    },

    onDragEnd(item, event) {
      if (item.draggable) {
        /**
         * Evento emitido ao cancelar ou finalizar a ação de dragging
         *
         * @property {Object} item Objeto referente ao item do card
         * @property {Object} event Evento do drag
         */
        this.$emit('dragend', {item, event})
        this.draggingItem = null
      }
    },

    onDragEnter(item, event) {
      /**
       * Evento emitido ao entrar na dropzone
       *
       * @property {Object} item Objeto referente ao item do card
       * @property {Object} event Evento do drag
       */
      this.$emit('dragenter', {item, event})
      this.draggingItem = item
    },

    onDragLeave(item, event) {
      /**
       * Evento emitido ao sair da dropzone
       *
       * @property {Object} item Objeto referente ao item do card
       * @property {Object} event Evento do drag
       */
      this.$emit('dragleave', {item, event})
      this.draggingItem = null
    },

    onDragOver(item, event) {
      /**
       * Evento emitido enquanto está na dropzone
       *
       * @property {Object} item Objeto referente ao item do card
       * @property {Object} event Evento do drag
       */
      this.$emit('dragover', {item, event})
      this.draggingItem = item
    },

    onDrop(item, event) {
      /**
       * Evento emitido quando a ação de drop é realizada com sucesso
       *
       * @property {Object} item Objeto referente ao item do card
       * @property {Object} event Evento do drag
       */
      this.$emit('drop', {item, event})
      this.draggingItem = null
    },

    /**
     * Evento emitido ao clicar no card
     *
     * @property {Object} item Objeto referente ao item do card
     */
    onClickCard(item) {
      this.$emit('click', item)
    },

    onClickAction(item, action) {
      /**
       * Evento emitido ao clicar nas ações do card
       *
       * @property {Object} item Objeto referente ao item do card
       * @property {Object} action Objeto referente á ação do card
       */
      this.$emit('action', {item, action})
    },
  },
}
</script>

<style lang="scss" scoped>
.card-list-container {
  display: flex;
  flex-direction: column;
}

.pagination {
  justify-content: center;
}

.skeleton-loader {
  border-radius: var(--component-card-radius);
  background: rgba(0, 0, 0, 0.12);
  height: 67.5px;
  width: 100%;
  position: relative;

  &::after {
    content: '';
    height: 100%;
    width: 100%;
    border-radius: var(--component-card-radius);
    background: linear-gradient(
      90deg,
      hsla(0, 0%, 100%, 0),
      hsla(0, 0%, 100%, 0.5),
      hsla(0, 0%, 100%, 0)
    );
    position: absolute;
    transform: translateX(-40%);
    left: 0;
    right: 0;
    top: 0;
    animation: loading 1.5s ease-in-out infinite alternate;
  }
}

@keyframes loading {
  100% {
    transform: translateX(40%);
  }
}
</style>
